<template>
  <div
      class="card"
      :class="{'is-read': inspected, 'is-dead': dead, 'is-selected': selected}"
      @click="$emit('select')"
  >
    <div class="card__plate" :style="{backgroundImage: `url(${resolvedIcon})`}"></div>
    <div class="card__veil"></div>
    <div v-if="dead" class="card__pall"></div>

    <FlowSignMark v-if="signed" :size="18" class="card__sign"/>

    <!-- What the inspection returned: who they are on the ladder, and which
         plane they are standing on. Absent until someone looks. -->
    <div v-if="hasStrip" class="card__strip">
      <span v-if="rank !== null" class="card__rank" :title="`Rank ${rank}`">{{ rank }}</span>
      <span v-if="dimension !== null" class="card__dim" :title="`Dimension ${dimension}`">D{{ dimension }}</span>
    </div>

    <div class="card__foot">
      <p class="card__name" :title="name">{{ name }}</p>
      <CharacterCardDetails v-if="hasReadout" :details="details" class="card__readout"/>
    </div>
  </div>
</template>

<script setup>
import {computed} from 'vue';
import CharacterCardDetails from './CharacterCardDetails.vue';
import FlowSignMark from '@/components/CharacterToken/FlowSignMark.vue';
import {useImageWithFallback} from '@/utils/imageFallback';
import {avatarPlaceholderUrl} from '@/utils/avatarPlaceholder';

const props = defineProps({
  name: {type: String, required: true},
  icon: {type: String, required: true},
  details: {type: Object, default: null},
  selected: {type: Boolean, default: false},
  dead: {type: Boolean, default: false},
  gender: {type: String, default: null},
  /** Character is Signed with Flow — under game master authority. */
  signed: {type: Boolean, default: false},
});

defineEmits(['select']);

const resolvedIcon = useImageWithFallback(
    computed(() => props.icon),
    avatarPlaceholderUrl(props.gender),
);

/* Location.vue writes the string "xx" wherever the inspection returned
   nothing, so neither field can be trusted to be a number. */
const reading = value => (Number.isFinite(Number(value)) && value !== '' && value !== null ? Number(value) : null);

const rank = computed(() => reading(props.details?.rankGrade));
const dimension = computed(() => reading(props.details?.dimension));
const hasReadout = computed(() => Object.keys(props.details?.attributes || {}).length > 0);
const hasStrip = computed(() => !props.dead && (rank.value !== null || dimension.value !== null));

/* A card is "read" once an inspection has landed on it. That is the one state
   change the roster has to make obvious: everything the card gains — the top
   edge, the rank strip, the vitals — arrives together at that moment. */
const inspected = computed(() => !props.dead && (rank.value !== null || dimension.value !== null || hasReadout.value));
</script>

<style scoped>
/* The roster card is a dossier plate, not a collectible: a dark frame, the
   portrait behind glass, and whatever has been learned etched over the bottom
   of it. Colour is spent on the readings only — the frame itself never
   competes with the scene it sits on.

   Sizing is unchanged; the card keeps its place in the holder's flex row. */
.card {
  position: relative;
  width: clamp(3rem, 5vw, 5rem);
  height: clamp(6rem, 10vw, 9rem);
  flex-shrink: 0;
  overflow: visible;
  border-radius: var(--pl-radius-sm, 0.3rem);
  background: var(--pl-surface-0, #0b0e11);
  box-shadow:
      0 0 0 1px rgba(121, 194, 212, 0.14),
      0 0.35rem 0.9rem rgba(0, 0, 0, 0.55);
  cursor: pointer;
  transition:
      transform var(--pl-duration, 0.15s) var(--pl-ease, ease),
      box-shadow var(--pl-duration, 0.15s) var(--pl-ease, ease);
}

.card.is-read {
  box-shadow:
      inset 0 1px 0 0 rgba(121, 194, 212, 0.55),
      0 0 0 1px rgba(121, 194, 212, 0.28),
      0 0.35rem 0.9rem rgba(0, 0, 0, 0.55);
}

.card:hover {
  transform: translateY(-0.2rem);
  box-shadow:
      inset 0 1px 0 0 rgba(95, 216, 242, 0.7),
      0 0 0 1px rgba(95, 216, 242, 0.55),
      0 0.6rem 1.1rem rgba(0, 0, 0, 0.6);
}

.card:focus-visible {
  outline: var(--focus-ring, 2px solid #79c2d4);
  outline-offset: var(--focus-offset, 2px);
}

.card__plate,
.card__veil,
.card__pall {
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.card__plate {
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
}

/* The portrait carries the name and the readings, so it is darkened where they
   sit rather than left to chance — a pale avatar used to swallow both. */
.card__veil {
  background: linear-gradient(
      180deg,
      rgba(11, 14, 17, 0.55) 0%,
      rgba(11, 14, 17, 0) 22%,
      rgba(11, 14, 17, 0.32) 45%,
      rgba(11, 14, 17, 0.88) 72%,
      rgba(11, 14, 17, 0.96) 100%
  );
}

.card__strip {
  position: absolute;
  top: 0.22rem;
  left: 0.28rem;
  right: 0.28rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: var(--pl-font-mono, ui-monospace, monospace);
  font-size: 0.46rem;
  line-height: 1;
}

.card__rank {
  color: var(--pl-accent-hi, #5fd8f2);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.card__dim {
  color: rgba(223, 233, 238, 0.5);
  letter-spacing: 0.06em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.card__sign {
  position: absolute;
  top: 0.9rem;
  right: 0.22rem;
  z-index: 2;
}

.card__foot {
  position: absolute;
  right: 0.28rem;
  bottom: 0.28rem;
  left: 0.28rem;
  z-index: 2;
}

.card__name {
  margin: 0 0 0.22rem;
  font-family: var(--pl-font-body, sans-serif);
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: 0.02em;
  color: var(--pl-text, #dfe9ee);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The readings arrive as one movement, so an inspection reads as a single
   thing happening rather than as three bars appearing. */
.card__readout {
  animation: readout-in var(--pl-duration-ambient, 0.45s) var(--pl-ease, ease) both;
}

@keyframes readout-in {
  from { opacity: 0; transform: translateY(0.2rem); }
  to { opacity: 1; transform: none; }
}

/* ---- dead ----
   A body tells you nothing new, so it loses the accent edge along with its
   colour. */
.card.is-dead {
  box-shadow:
      0 0 0 1px rgba(223, 233, 238, 0.1),
      0 0.2rem 0.6rem rgba(0, 0, 0, 0.5);
}

.card.is-dead .card__plate {
  filter: grayscale(100%) brightness(0.82);
}

.card.is-dead .card__name {
  color: var(--pl-text-muted, #8b9aa3);
}

/* The critical-health pulse is a warning about someone who can still be saved.
   On a body it is noise. */
.card.is-dead :deep(.readout__fill) {
  animation: none;
}

/* A mark on the body, not a poster over it. The death plate is drawn small
   and colourless so the face underneath is still recognisable — a player has
   to be able to tell WHO went down, not only that someone did. */
.card__pall {
  z-index: 1;
  background: url('@/assets/images/avatar/death.webp') no-repeat center 40%;
  background-size: 72%;
  opacity: 0.78;
}

/* ---- selected ----
   The card itself is the marker: it stands forward out of the row and takes a
   full accent frame. A separate glyph used to hang off it pointing at it,
   which at this size was a bigger object than the card and crossed whatever it
   overlapped. Standing proud costs no space and covers nothing.

   Declared after :hover so a selection stays the strongest state in the row
   even while the pointer is somewhere else. */
.card.is-selected {
  transform: translateY(-0.4rem);
  box-shadow:
      0 0 0 2px var(--pl-accent-hi, #5fd8f2),
      0 0.7rem 1.3rem rgba(0, 0, 0, 0.7);
}

.card.is-selected:hover {
  transform: translateY(-0.55rem);
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .card__readout {
    animation: none;
    transition: none;
  }
}
</style>
